import { useState } from "react"
import { router, usePage } from "@inertiajs/react"

interface ContactFormProps {
  // Class of the <form> element: the about page passes "w-full", the contact
  // page passes nothing — mirroring the two Slim views.
  formClassName?: string
  // Class of the submit input: "btn btn-primary ml-auto block" on about,
  // "btn btn-primary" on contact.
  submitClassName: string
}

interface PageProps {
  flash: { notice?: string; alert?: string }
  errors?: Record<string, string | string[]>
  [key: string]: unknown
}

const FIELDS = ["full_name", "email", "content", "nickname"] as const
type FieldName = (typeof FIELDS)[number]

const EMPTY_FORM: Record<FieldName, string> = {
  full_name: "",
  email: "",
  content: "",
  nickname: "",
}

// Faithful React port of the simple_form contact form shared by
// kenjosset/about.html.slim and kenjosset/contact.html.slim. The markup mirrors
// SimpleForm's :floating_label wrapper (form-group > form-input > floating-label)
// so the existing CSS applies unchanged. `nickname` stays a hidden honeypot.
export default function ContactForm({ formClassName, submitClassName }: ContactFormProps) {
  const { props } = usePage<PageProps>()
  const inertiaErrors: Record<string, string | string[]> = props.errors ?? {}

  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [submitting, setSubmitting] = useState(false)

  // Inertia sends either a string or an array of messages per field; the Slim
  // form displayed the first one.
  const errors: Partial<Record<string, string>> = Object.fromEntries(
    Object.entries(inertiaErrors)
      .map(([key, value]) => {
        if (Array.isArray(value)) return value.length > 0 ? [key, value[0]] : null
        if (typeof value === "string" && value.length > 0) return [key, value]
        return null
      })
      .filter((entry): entry is [string, string] => entry !== null)
  )

  const update = (field: FieldName, value: string) =>
    setForm((current) => ({ ...current, [field]: value }))

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    router.post(
      "/send_message",
      { contact_form: form },
      {
        onSuccess: () => setForm({ ...EMPTY_FORM }),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  const groupClass = (field: FieldName) =>
    errors[field] ? "form-group relative has-error" : "form-group relative"

  return (
    <form onSubmit={handleSubmit} className={formClassName}>
      <div className="text-input mb-3">
        <div className={groupClass("full_name")}>
          <input
            className="form-input"
            type="text"
            id="contact_form_full_name"
            name="contact_form[full_name]"
            placeholder="Nom & Prénom"
            value={form.full_name}
            onChange={(event) => update("full_name", event.target.value)}
          />
          <label className="floating-label" htmlFor="contact_form_full_name">
            Nom &amp; Prénom
          </label>
          {errors.full_name && <span className="error">{errors.full_name}</span>}
        </div>
      </div>

      <div className="text-input mb-3">
        <div className={groupClass("email")}>
          <input
            className="form-input"
            type="email"
            id="contact_form_email"
            name="contact_form[email]"
            placeholder="Email"
            value={form.email}
            onChange={(event) => update("email", event.target.value)}
          />
          <label className="floating-label" htmlFor="contact_form_email">
            Email
          </label>
          {errors.email && <span className="error">{errors.email}</span>}
        </div>
      </div>

      {/* Honeypot: hidden from humans, filled by bots — the server rejects it. */}
      <div className="text-input mb-3 hidden">
        <div className={groupClass("nickname")}>
          <input
            className="form-input"
            type="text"
            id="contact_form_nickname"
            name="contact_form[nickname]"
            placeholder="Nickname"
            tabIndex={-1}
            autoComplete="off"
            value={form.nickname}
            onChange={(event) => update("nickname", event.target.value)}
          />
          <label className="floating-label" htmlFor="contact_form_nickname">
            Nickname
          </label>
        </div>
      </div>

      <div className="text-input mb-3">
        <div className={groupClass("content")}>
          <textarea
            className="form-input"
            rows={5}
            id="contact_form_content"
            name="contact_form[content]"
            placeholder="Détails"
            value={form.content}
            onChange={(event) => update("content", event.target.value)}
          />
          <label className="floating-label" htmlFor="contact_form_content">
            Détails
          </label>
          {errors.content && <span className="error">{errors.content}</span>}
        </div>
      </div>

      <input type="submit" name="commit" value="Envoyer" className={submitClassName} disabled={submitting} />
    </form>
  )
}
